/**
 * `<site-search>` — site-wide search palette with a trigger button.
 *
 * NOT published to JSR. Lives here because the search index (search.json)
 * is generated by the site's build script and the result-row rendering
 * is tailored to the docs site's page categories (page / blog /
 * component).
 *
 * Renders a small `⌘K`-style trigger that, when clicked or activated
 * with Cmd/Ctrl+K from anywhere on the page, opens a modal palette
 * with a search input + filtered result rows. Esc closes. ↑/↓ moves
 * focus through results. Enter navigates to the focused row.
 *
 * Props:
 *   base   string — relative path prefix for fetching search.json
 *                   and for link hrefs ("" on top-level pages,
 *                   "../" on blog/* and components/*).
 */

import { build, describe } from "@ra9/tan-compose";

const TAG = "site-search";

export const tagName: string = TAG;

interface SearchDoc {
  url: string;
  title: string;
  description: string;
  category: string;
  tag?: string;
  date?: string;
  text: string;
}

interface ScoredResult {
  doc: SearchDoc;
  score: number;
}

let cachedDocs: SearchDoc[] | null = null;
let inflight: Promise<SearchDoc[]> | null = null;

function loadDocs(base: string): Promise<SearchDoc[]> {
  if (cachedDocs) return Promise.resolve(cachedDocs);
  if (inflight) return inflight;
  const url = `${base}search.json`;
  inflight = fetch(url)
    .then((r) => r.json())
    .then((j: { docs: SearchDoc[] }) => {
      cachedDocs = j.docs ?? [];
      return cachedDocs;
    })
    .catch((err) => {
      console.warn("[site-search] failed to load index:", err);
      cachedDocs = [];
      return cachedDocs;
    });
  return inflight;
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function search(query: string, docs: SearchDoc[]): ScoredResult[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const terms = q.split(/\s+/).filter(Boolean).map((t) => ({
    raw: t,
    re: new RegExp(escapeRegex(t), "i"),
  }));
  const scored: ScoredResult[] = [];
  for (const doc of docs) {
    const title = doc.title.toLowerCase();
    const desc = (doc.description ?? "").toLowerCase();
    const text = (doc.text ?? "").toLowerCase();
    let score = 0;
    for (const t of terms) {
      const term = t.raw;
      if (title === term) score += 50;
      if (title.startsWith(term)) score += 20;
      if (title.includes(term)) score += 10;
      if (desc.includes(term)) score += 5;
      if (text.includes(term)) score += 1;
    }
    const allHit = terms.every((t) =>
      t.re.test(doc.title) || t.re.test(desc) || t.re.test(text)
    );
    if (!allHit) continue;
    if (score === 0) continue;
    if (doc.category === "blog" && doc.date) {
      const days = (Date.now() - new Date(doc.date).getTime()) /
        (1000 * 60 * 60 * 24);
      if (days < 30) score += 3;
    }
    scored.push({ doc, score });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, 12);
}

function findSnippet(query: string, text: string, max = 140): string {
  const q = query.trim().toLowerCase().split(/\s+/)[0];
  if (!q) return text.slice(0, max);
  const lower = text.toLowerCase();
  const idx = lower.indexOf(q);
  if (idx === -1) return text.slice(0, max);
  const start = Math.max(0, idx - 40);
  const end = Math.min(text.length, start + max);
  const prefix = start > 0 ? "… " : "";
  const suffix = end < text.length ? " …" : "";
  return prefix + text.slice(start, end) + suffix;
}

function esc(s: unknown): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function highlight(text: string, query: string): string {
  const q = query.trim();
  if (!q) return esc(text);
  const terms = q.split(/\s+/).filter(Boolean);
  let out = esc(text);
  for (const term of terms) {
    const re = new RegExp(`(${escapeRegex(esc(term))})`, "gi");
    out = out.replace(re, "<mark>$1</mark>");
  }
  return out;
}

interface MinCtx {
  host: HTMLElement;
  setState: (k: string, v: unknown) => void;
}

function closePalette(ctx: MinCtx): void {
  ctx.setState("open", false);
  ctx.setState("query", "");
  ctx.setState("results", []);
  ctx.setState("focusIdx", 0);
}

// Declared BEFORE build() because customElements.define() synchronously
// upgrades any <site-search> already in the DOM — that triggers the
// template, which reads STYLE. esbuild's minifier converts `const` to
// `var` so a STYLE declared after build() is hoisted-but-undefined when
// the template first runs, producing literal "undefined" in the output.
// See kit/components/button.ts for the same pattern.
const STYLE = `
        <style>
          :host { display: inline-block; }

          .trigger {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            padding: 6px 10px;
            font-family: var(--tc-font-sans, "Inter", system-ui, sans-serif);
            font-size: 0.86rem;
            color: var(--tc-color-ink-soft, #4a5061);
            background: var(--tc-color-surface, #ffffff);
            border: 1px solid var(--tc-color-rule, #ece5d3);
            border-radius: var(--tc-radius-md, 8px);
            cursor: pointer;
            min-width: 200px;
            transition: border-color 0.15s ease, color 0.15s ease;
          }
          .trigger:hover {
            border-color: var(--tc-color-accent, #a16939);
            color: var(--tc-color-ink, #14171f);
          }
          .trigger-icon { width: 14px; height: 14px; flex: 0 0 auto; }
          .trigger-label { flex: 1 1 auto; text-align: left; }
          .trigger-kbd {
            font-family: var(--tc-font-mono, "JetBrains Mono", monospace);
            font-size: 0.7rem;
            color: var(--tc-color-ink-muted, #6b7280);
            background: var(--tc-color-bg, #faf8f3);
            border: 1px solid var(--tc-color-rule, #ece5d3);
            border-radius: 4px;
            padding: 1px 5px;
            line-height: 1.4;
          }

          dialog.modal {
            background: var(--tc-color-surface, #ffffff);
            border: 1px solid var(--tc-color-rule, #ece5d3);
            border-radius: var(--tc-radius-lg, 12px);
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
            padding: 0;
            width: calc(100% - 32px);
            max-width: 640px;
            max-height: calc(100vh - 120px);
            inset: 96px auto auto 50%;
            transform: translateX(-50%);
            margin: 0;
            overflow: hidden;
            font-family: var(--tc-font-sans, "Inter", system-ui, sans-serif);
            color: var(--tc-color-ink, #14171f);
          }
          dialog.modal[open] {
            display: flex;
            flex-direction: column;
          }
          dialog.modal::backdrop {
            background: rgba(20, 23, 31, 0.45);
            backdrop-filter: blur(2px);
          }

          .modal-head {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 14px 16px;
            border-bottom: 1px solid var(--tc-color-rule, #ece5d3);
          }
          .search-icon {
            width: 18px;
            height: 18px;
            color: var(--tc-color-ink-muted, #6b7280);
            flex: 0 0 auto;
          }
          .search-input {
            flex: 1 1 auto;
            border: none;
            outline: none;
            font: inherit;
            font-size: 1rem;
            background: transparent;
            color: inherit;
            min-width: 0;
          }
          .close {
            font: inherit;
            font-size: 0.74rem;
            font-family: var(--tc-font-mono, monospace);
            background: var(--tc-color-bg, #faf8f3);
            border: 1px solid var(--tc-color-rule, #ece5d3);
            border-radius: 4px;
            color: var(--tc-color-ink-muted, #6b7280);
            padding: 3px 8px;
            cursor: pointer;
          }
          .close:hover { color: var(--tc-color-ink, #14171f); }

          .results {
            flex: 1 1 auto;
            overflow-y: auto;
            padding: 4px;
          }
          .empty {
            padding: 28px;
            text-align: center;
            color: var(--tc-color-ink-muted, #6b7280);
            font-size: 0.92rem;
          }

          .row {
            display: flex;
            align-items: flex-start;
            gap: 10px;
            padding: 10px 12px;
            border-radius: var(--tc-radius-md, 8px);
            text-decoration: none;
            color: inherit;
            cursor: pointer;
          }
          .row.focused, .row:hover {
            background: var(--tc-color-accent-soft, #efe2cf);
          }
          .row-cat {
            flex: 0 0 auto;
            font-family: var(--tc-font-mono, monospace);
            font-size: 0.66rem;
            text-transform: uppercase;
            letter-spacing: 0.06em;
            padding: 3px 7px;
            border-radius: 999px;
            background: var(--tc-color-bg, #faf8f3);
            color: var(--tc-color-ink-muted, #6b7280);
            border: 1px solid var(--tc-color-rule, #ece5d3);
            margin-top: 2px;
          }
          .row-cat.blog { color: var(--tc-color-accent, #a16939); border-color: var(--tc-color-accent-soft, #efe2cf); }
          .row-cat.component { color: #5b6cf0; border-color: #d0d5f0; }
          .row-cat.page { color: var(--tc-color-ink-soft, #4a5061); }
          .row-main { flex: 1 1 auto; min-width: 0; }
          .row-title {
            font-size: 0.96rem;
            font-weight: 600;
            margin-bottom: 2px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .row-desc {
            font-size: 0.84rem;
            color: var(--tc-color-ink-soft, #4a5061);
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            margin-bottom: 2px;
          }
          .row-snippet {
            font-size: 0.78rem;
            color: var(--tc-color-ink-muted, #6b7280);
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          mark {
            background: var(--tc-color-accent-soft, #efe2cf);
            color: var(--tc-color-accent-hover, #8a572d);
            padding: 0 2px;
            border-radius: 2px;
          }

          .footer {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 10px 16px;
            border-top: 1px solid var(--tc-color-rule, #ece5d3);
            background: var(--tc-color-bg, #faf8f3);
            font-size: 0.78rem;
            color: var(--tc-color-ink-muted, #6b7280);
          }
          .hint {
            display: inline-flex;
            align-items: center;
            gap: 4px;
          }
          .hint kbd {
            font-family: var(--tc-font-mono, monospace);
            font-size: 0.7rem;
            background: var(--tc-color-surface, #ffffff);
            border: 1px solid var(--tc-color-rule, #ece5d3);
            border-radius: 3px;
            padding: 1px 5px;
            line-height: 1.4;
          }
          .count { margin-left: auto; }

          @media (max-width: 600px) {
            .trigger { min-width: 120px; }
            .trigger-label { display: none; }
            dialog.modal {
              inset: 40px 8px auto 8px;
              transform: none;
              width: auto;
              max-width: none;
            }
            .row-snippet { display: none; }
          }
        </style>
`;

build(
  TAG,
  describe({
    props: {
      base: { type: "string", default: "" },
    },
    styles: { display: "inline-block" },
    refs: {
      input: ".search-input",
      results: ".results",
      dialog: "dialog.modal",
    },
    template: ({ props, state }) => {
      const base = String(props.base ?? "");
      const query = String(state.query ?? "");
      const focusIdx = Number(state.focusIdx ?? 0);
      const results = (state.results as ScoredResult[] | undefined) ?? [];

      const trigger = `
        <button type="button" class="trigger" aria-label="Search the site (⌘K)">
          <svg class="trigger-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <span class="trigger-label">Search</span>
          <kbd class="trigger-kbd" aria-hidden="true">⌘K</kbd>
        </button>
      `;

      const resultsHtml = query.trim() === ""
        ? `<div class="empty">Start typing to search the site — docs, components, blog posts, examples.</div>`
        : results.length === 0
        ? `<div class="empty">No results for "${
          esc(query)
        }". Try a shorter query.</div>`
        : results
          .map((r, i) => {
            const cls = i === focusIdx ? "row focused" : "row";
            const href = base + r.doc.url.replace(/^\//, "");
            const snippet = findSnippet(query, r.doc.text);
            return `
              <a class="${cls}" data-index="${i}" href="${esc(href)}">
                <span class="row-cat ${esc(r.doc.category)}">${
              esc(r.doc.category)
            }</span>
                <div class="row-main">
                  <div class="row-title">${highlight(r.doc.title, query)}</div>
                  <div class="row-desc">${
              highlight(r.doc.description, query)
            }</div>
                  <div class="row-snippet">${highlight(snippet, query)}</div>
                </div>
              </a>
            `;
          })
          .join("");

      // The dialog is always present in markup. afterRender syncs its
      // open state with state.open by calling showModal() / close().
      // showModal() puts the dialog in the browser top-layer, escaping
      // every ancestor stacking context (in particular site-nav's
      // .topbar, which creates one via backdrop-filter).
      const dialog = `
        <dialog class="modal" aria-label="Site search">
          <div class="modal-head">
            <svg class="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              type="text"
              class="search-input"
              placeholder="Search docs, components, blog…"
              value="${esc(query)}"
              aria-label="Search"
              aria-autocomplete="list"
              autocomplete="off"
              spellcheck="false"
            />
            <button type="button" class="close" aria-label="Close">Esc</button>
          </div>
          <div class="results" role="listbox">${resultsHtml}</div>
          <div class="footer">
            <span class="hint"><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
            <span class="hint"><kbd>↵</kbd> open</span>
            <span class="hint"><kbd>esc</kbd> close</span>
            <span class="count">${
        results.length > 0
          ? `${results.length} result${results.length === 1 ? "" : "s"}`
          : ""
      }</span>
          </div>
        </dialog>
      `;
      return trigger + dialog + STYLE;
    },
    events: {
      "click .trigger": (_e, ctx) => {
        const host = ctx.host as HTMLElement & { base?: string };
        const base = String(host.base ?? "");
        ctx.setState("open", true);
        ctx.setState("query", "");
        ctx.setState("focusIdx", 0);
        ctx.setState("results", []);
        loadDocs(base);
      },
      "click .close": (_e, ctx) => closePalette(ctx),
      "input .search-input": (e, ctx) => {
        const value = (e.target as HTMLInputElement).value;
        const host = ctx.host as HTMLElement & { base?: string };
        const base = String(host.base ?? "");
        ctx.setState("query", value);
        ctx.setState("focusIdx", 0);
        if (cachedDocs) {
          ctx.setState("results", search(value, cachedDocs));
        } else {
          loadDocs(base).then((docs) => {
            ctx.setState("results", search(value, docs));
          });
        }
      },
      "keydown .search-input": (e, ctx) => {
        const ev = e as KeyboardEvent;
        const results =
          (ctx.getState("results") as ScoredResult[] | undefined) ?? [];
        const idx = Number(ctx.getState("focusIdx") ?? 0);
        if (ev.key === "ArrowDown") {
          ev.preventDefault();
          if (results.length === 0) return;
          ctx.setState("focusIdx", Math.min(results.length - 1, idx + 1));
          return;
        }
        if (ev.key === "ArrowUp") {
          ev.preventDefault();
          if (results.length === 0) return;
          ctx.setState("focusIdx", Math.max(0, idx - 1));
          return;
        }
        if (ev.key === "Enter") {
          if (results.length === 0) return;
          ev.preventDefault();
          const target = results[idx];
          if (target) {
            const host = ctx.host as HTMLElement & { base?: string };
            const base = String(host.base ?? "");
            const href = base + target.doc.url.replace(/^\//, "");
            globalThis.location.href = href;
          }
          return;
        }
        // Escape is handled by the dialog itself (fires the `close`
        // event, see afterRender). Leaving this here would double-handle
        // and fight the dialog's native cancel.
      },
      "mouseover .row": (e, ctx) => {
        const row = (e.target as HTMLElement).closest(".row") as
          | HTMLElement
          | null;
        if (!row) return;
        const i = Number(row.dataset.index);
        if (!Number.isNaN(i)) ctx.setState("focusIdx", i);
      },
    },
    afterMount() {
      const host = this as unknown as HTMLElement & {
        base?: string;
        getState?: (k: string) => unknown;
        setState?: (k: string, v: unknown) => void;
      };
      const handler = (e: KeyboardEvent) => {
        if (
          e.key.toLowerCase() === "k" && (e.metaKey || e.ctrlKey) && !e.altKey
        ) {
          e.preventDefault();
          if (!host.setState) return;
          host.setState("open", true);
          host.setState("query", "");
          host.setState("focusIdx", 0);
          host.setState("results", []);
          loadDocs(String(host.base ?? ""));
          return;
        }
        if (e.key === "/" && !e.metaKey && !e.ctrlKey && !e.altKey) {
          const ae = document.activeElement as HTMLElement | null;
          const tag = ae?.tagName.toLowerCase();
          const isEditing = tag === "input" || tag === "textarea" ||
            ae?.isContentEditable === true;
          if (isEditing) return;
          e.preventDefault();
          if (!host.setState) return;
          host.setState("open", true);
          host.setState("query", "");
          host.setState("focusIdx", 0);
          host.setState("results", []);
          loadDocs(String(host.base ?? ""));
        }
      };
      document.addEventListener("keydown", handler);
      (host as unknown as { _searchKeyHandler?: typeof handler })
        ._searchKeyHandler = handler;
    },
    unmount() {
      const host = this as unknown as {
        _searchKeyHandler?: (e: KeyboardEvent) => void;
      };
      if (host._searchKeyHandler) {
        document.removeEventListener("keydown", host._searchKeyHandler);
      }
    },
    afterRender() {
      const host = this as unknown as HTMLElement & {
        shadowRoot?: ShadowRoot | null;
        refs?: {
          input?: HTMLInputElement | null;
          dialog?: HTMLDialogElement | null;
        };
        getState?: (k: string) => unknown;
        setState?: (k: string, v: unknown) => void;
      };
      const dialog = host.refs?.dialog ?? null;
      if (!dialog) return;
      const open = host.getState ? !!host.getState("open") : false;

      // Each render produces a fresh <dialog> element (innerHTML
      // replaces the previous one). Its initial state is closed, so we
      // always have to (re)open it via showModal() when state.open is
      // true. Attach `close` / backdrop-click listeners here too — they
      // get GC'd with the old dialog.
      if (open && !dialog.open) {
        dialog.showModal();
        const input = host.refs?.input ?? null;
        input?.focus();
        dialog.addEventListener("close", () => {
          if (host.getState?.("open")) {
            host.setState?.("open", false);
            host.setState?.("query", "");
            host.setState?.("results", []);
            host.setState?.("focusIdx", 0);
          }
        });
        dialog.addEventListener("click", (e) => {
          if (e.target === dialog) dialog.close();
        });
        return;
      }
      if (!open && dialog.open) {
        dialog.close();
        return;
      }
      if (open) {
        const input = host.refs?.input ?? null;
        if (input && host.shadowRoot?.activeElement !== input) input.focus();
      }
    },
  }),
);
